"use client";

/**
 * MemberHoverAvatar
 *
 * A MemberAvatar that behaves like an @mention: hovering shows the shared
 * `MentionHoverCard` (name / avatar / email / local time) and clicking it
 * navigates to that user's profile (`/userprofile/{userId}`).
 *
 * Comment authors are only stored by email, so we resolve their userId from
 * the workspace directory via `getMemberId(email)`. When the author isn't a
 * resolvable workspace member (e.g. an external commenter), we fall back to a
 * plain, non-interactive avatar.
 */

import { useRouter } from "next/navigation";

import { useWorkspaceContext } from "@/contexts/workspaceContext";
import { MentionHoverCard } from "@/components/tailwind/mention-hover-card";

import { MemberAvatar } from "./memberAvatar";

interface MemberHoverAvatarProps {
  name?: string;
  email?: string;
  /** Known userId — used directly when available; otherwise resolved from email. */
  userId?: string;
  /** Classes for the avatar circle (size / bg / rounding). */
  className?: string;
  /** Layout classes for the inline trigger wrapper (e.g. flex-shrink-0). */
  wrapperClassName?: string;
}

export function MemberHoverAvatar({
  name,
  email,
  userId: userIdProp,
  className,
  wrapperClassName,
}: MemberHoverAvatarProps) {
  const router = useRouter();
  const { getMemberId, getMemberName } = useWorkspaceContext();

  const userId = userIdProp || getMemberId(email);
  const displayName = getMemberName(userIdProp) || getMemberName(email) || name || email || "";

  const avatar = (
    <MemberAvatar name={displayName} email={email} userId={userId} className={className} />
  );

  // Unknown / external author → plain avatar (no hover card, no navigation).
  if (!userId) {
    return wrapperClassName ? <span className={wrapperClassName}>{avatar}</span> : avatar;
  }

  return (
    <MentionHoverCard
      userId={userId}
      userName={displayName}
      userEmail={email}
      className={wrapperClassName}
    >
      <span
        className="cursor-pointer"
        onClick={(e) => {
          e.stopPropagation();
          router.push(`/userprofile/${userId}`);
        }}
      >
        {avatar}
      </span>
    </MentionHoverCard>
  );
}
